import { cn } from "@/lib/utils";
import * as flags from "country-flag-icons/react/3x2";
import { ReactNode, useEffect, useRef } from "react";
import { useTranslations, useLocale } from "next-intl";

interface LanguageItem {
  id: string;
  code: string;
  name: string;
  nativeName: string;
  countryCode: string;
}

interface LanguageScrollProps {
  languages: LanguageItem[];
  className?: string;
  onShowAll: () => void;
}

// Helper function to get a flag component by country code
const getFlagComponent = (countryCode: string): ReactNode => {
  const FlagComponent = flags[countryCode as keyof typeof flags];
  return FlagComponent ? (
    <FlagComponent className="w-9 h-6 rounded shadow-sm" />
  ) : null;
};

export function LanguageScroll({
  languages,
  className,
  onShowAll,
}: LanguageScrollProps) {
  const t = useTranslations("HomePage");
  const locale = useLocale();
  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Handle continuous scroll manually for better control
    if (!scrollRef.current || !containerRef.current) return;

    let animationId: number;
    let scrollPosition = 0;
    const scrollSpeed = 0.7; // pixels per frame - adjusted for smoother motion
    const firstSetHeight =
      scrollRef.current.children[0]?.clientHeight * languages.length || 0;

    const animate = () => {
      if (!scrollRef.current || !containerRef.current) return;

      scrollPosition += scrollSpeed;

      // If we've scrolled through the first set, reset position to create illusion of infinity
      if (scrollPosition >= firstSetHeight) {
        // Reset to the beginning of the second set (which is identical to the first)
        scrollPosition = 0;
      }

      scrollRef.current.style.transform = `translateY(-${scrollPosition}px)`;
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [languages]);

  // Sort languages based on locale
  const sortedLanguages = [...languages];

  if (locale === "ja") {
    // For Japanese locale, prioritize these languages in order of importance for Japanese users
    const priorityLanguages = [
      "japanese",
      "english",
      "chinese-simplified",
      "chinese-traditional",
      "korean",
      "cantonese",
      "thai",
      "vietnamese",
      "indonesian",
      "filipino",
      "french",
      "german",
      "spanish",
      "italian",
      "russian",
    ];

    // First sort all languages alphabetically by name
    sortedLanguages.sort((a, b) => a.name.localeCompare(b.name));

    // Then move priority languages to the top (in reverse order to maintain the desired sequence)
    priorityLanguages.reverse().forEach((langCode) => {
      const index = sortedLanguages.findIndex((lang) => lang.code === langCode);
      if (index !== -1) {
        const [lang] = sortedLanguages.splice(index, 1);
        sortedLanguages.unshift(lang);
      }
    });
  } else {
    // For English locale, sort alphabetically
    sortedLanguages.sort((a, b) => a.name.localeCompare(b.name));
  }

  // Triple the languages to ensure smooth scroll transition
  const duplicatedLanguages = [
    ...sortedLanguages,
    ...sortedLanguages,
    ...sortedLanguages,
  ];

  return (
    <>
      <div className={cn("flex items-center justify-center mt-6", className)}>
        <div
          ref={containerRef}
          className="relative overflow-hidden h-[425px] [mask-image:linear-gradient(to_bottom,transparent_0%,black_5%,black_95%,transparent_100%)]"
          style={{ width: "350px" }}
        >
          <div
            ref={scrollRef}
            className="grid gap-5 grid-cols-1"
            style={{ willChange: "transform" }}
          >
            {duplicatedLanguages.map((lang, index) => (
              <div
                key={`${lang.id}-${index}`}
                className="group flex items-center gap-4 cursor-pointer rounded-md border border-border/40 bg-gradient-to-b from-background/80 to-muted/80 p-4 shadow-md transition-all duration-300 ease-in-out hover:scale-105 hover:-translate-x-1 hover:-translate-y-1 hover:shadow-xl dark:border-border"
              >
                <div className="h-10 w-10 flex items-center justify-center">
                  {getFlagComponent(lang.countryCode)}
                </div>
                <div>
                  <p className="font-medium font-display text-foreground/80 transition-colors group-hover:text-foreground">
                    {lang.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {lang.nativeName}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-center mt-8">
        <button
          onClick={onShowAll}
          className="px-4 py-2 rounded-md border border-border/40 bg-background text-foreground/80 hover:text-foreground transition-colors flex items-center gap-2 text-sm font-medium shadow-sm hover:shadow"
        >
          <span>{t("showAllLanguages")}</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
        </button>
      </div>
    </>
  );
}
